import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Flame, MapPin, CalendarDays, Timer, Trophy, DollarSign, Zap, Users } from 'lucide-react';
import { STUDIO, VIDEOS, TRAINERS, HYROX, NEIGHBORHOODS, SCHEDULE } from '../data/mock';
import { usePageMeta } from '../components/Layout';
import { Reveal, SectionHeader, PageHero, CloseCTA, ProofStrip, FAQAccordion, JsonLd } from '../components/Shared';
import { useSchedule } from '../lib/api';

const SITE = 'https://pitfitworkout.com';

function buildJsonLd(hyroxClasses) {
  const dayMap = {
    Monday: 'https://schema.org/Monday', Tuesday: 'https://schema.org/Tuesday', Wednesday: 'https://schema.org/Wednesday',
    Thursday: 'https://schema.org/Thursday', Friday: 'https://schema.org/Friday', Saturday: 'https://schema.org/Saturday', Sunday: 'https://schema.org/Sunday',
  };
  const events = hyroxClasses.map((c) => ({
    '@type': 'Event',
    name: `${c.name} — HYROX Class in Dallas`,
    description: `Coached ${c.name} class at PITFIT in Uptown Dallas, led by ${c.coach}. Part of PITFIT's weekly HYROX training program.`,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    organizer: { '@type': 'Organization', name: 'PITFIT', url: SITE },
    performer: { '@type': 'Person', name: c.coach },
    location: {
      '@type': 'Place',
      name: 'PITFIT — Travis Walk',
      address: { '@type': 'PostalAddress', streetAddress: '4514 Travis St', addressLocality: 'Dallas', addressRegion: 'TX', postalCode: '75205', addressCountry: 'US' },
    },
    eventSchedule: { '@type': 'Schedule', byDay: dayMap[c.day], startTime: c.time, repeatFrequency: 'P1W' },
    offers: { '@type': 'Offer', price: '15', priceCurrency: 'USD', description: '$15 first class for new clients — no membership required. Single classes $36; packs and memberships available.', availability: 'https://schema.org/InStock', url: `${SITE}/book` },
  }));

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE}/hyrox-classes-dallas`,
        url: `${SITE}/hyrox-classes-dallas`,
        name: 'HYROX Classes in Dallas, TX | PITFIT',
        description: 'Coached HYROX classes six days a week in Uptown Dallas. Real instructors, race-specific training, $15 first class.',
        isPartOf: { '@type': 'WebSite', name: 'PITFIT', url: SITE },
        about: { '@type': 'Thing', name: 'HYROX training' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'HYROX Classes Dallas', item: `${SITE}/hyrox-classes-dallas` },
        ],
      },
      {
        '@type': 'VideoObject',
        name: 'HYROX training at PITFIT Dallas',
        description: 'Sandbag and station training inside the PITFIT parking-garage studio at Travis Walk, Uptown Dallas.',
        thumbnailUrl: `${SITE}/videos/pitfit-sandbags.jpg`,
        contentUrl: `${SITE}/videos/pitfit-sandbags.mp4`,
        uploadDate: '2026-07-01',
      },
      {
        '@type': 'FAQPage',
        mainEntity: HYROX.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      ...events,
    ],
  };
}

function hyroxFrom(week) {
  const out = [];
  (week || []).forEach((d) => {
    d.classes.forEach((c) => {
      if (c.name.toUpperCase().includes('HYROX')) out.push({ day: d.day, ...c });
    });
  });
  return out;
}

export default function Hyrox() {
  usePageMeta(
    'HYROX Classes in Dallas, TX — Coached 6 Days a Week',
    'HYROX classes in Dallas at PITFIT, Uptown\u2019s Travis Walk garage studio: coached HYROX Total Body, Strength & Endurance and Mobility sessions 6 days a week with Evan Conrad, Sara Yingling and founder Chris Wiese. $15 first class — no consultation required.'
  );

  const { schedule } = useSchedule();
  const week = schedule || SCHEDULE;
  const hyroxClasses = hyroxFrom(week);
  const staticHyrox = hyroxFrom(SCHEDULE);
  const coaches = HYROX.coaches.map((slug) => TRAINERS.find((t) => t.slug === slug)).filter(Boolean);

  const whyUs = [
    { icon: DollarSign, t: '$15 first class. Really.', d: 'No consultation call, no sales tour, no inflated trial pass. Book online for $15, show up, train. Then pick from 2 weeks unlimited for $50, class packs or memberships.' },
    { icon: Zap, t: 'Book in 30 seconds', d: 'Pick a HYROX class from the live schedule and reserve instantly. No intake forms, no waiting on a callback.' },
    { icon: CalendarDays, t: 'HYROX 6 days a week', d: '12 HYROX classes across Monday–Saturday, split into total body, lower body, upper body, and mobility emphases so your week periodizes itself.' },
    { icon: Users, t: 'Named coaches, every class', d: 'Evan Conrad, Sara Yingling and founder Chris Wiese own their slots on the schedule — you always know exactly who is coaching you.' },
    { icon: Flame, t: 'HIIT + HYROX, one membership', d: 'Build your engine in non-stop 45- and 60-minute HIIT classes and your race-specific strength in HYROX sessions — same garage, same membership.' },
    { icon: MapPin, t: 'Uptown, not a warehouse district', d: 'Travis Walk, 4514 Travis St — minutes from Knox-Henderson, Highland Park, the Park Cities and Downtown, with free parking in the building.' },
  ];

  return (
    <>
      <JsonLd data={buildJsonLd(staticHyrox)} />

      <PageHero
        tag="HYROX Training · Uptown Dallas"
        title="HYROX classes in Dallas, TX"
        sub="Coached HYROX training six days a week inside the Travis Walk garage — sleds, sandbag lunges, wall balls and compromised running, programmed for race day. $15 first class, no consultation required."
        video={VIDEOS.sandbags}
      />
      <ProofStrip />

      {/* FAST FACTS */}
      <section className="section-pad !py-12">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            ['12', 'HYROX classes / week'],
            ['3', 'dedicated HYROX coaches'],
            ['9', 'race stations trained'],
            ['$15', 'your first class'],
          ].map(([n, l], i) => (
            <Reveal key={l} delay={i * 60}>
              <div className="glass-card p-6 text-center h-full">
                <p className="font-display font-bold text-4xl text-white">{n}</p>
                <p className="font-mono-pf text-[11px] uppercase tracking-[0.12em] text-muted-pf mt-2">{l}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHAT IS HYROX */}
      <section className="section-pad" style={{ background: 'var(--pf-bg2)' }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="mono-label mb-4">First Principles</p>
            <h2 className="h-section text-white">What is HYROX?</h2>
            <p className="text-muted-pf mt-5 text-[17px] leading-relaxed">
              HYROX is a standardized indoor fitness race: run 1 km, hit a functional station, and repeat eight times. The stations never change — SkiErg, sled push, sled pull, burpee broad jumps, rowing, farmers carry, sandbag lunges, wall balls — so every race in the world is the same test, and every finish time is a real benchmark.
            </p>
            <p className="text-muted-pf mt-4 text-[17px] leading-relaxed">
              That fixed format is exactly why it can be trained with precision. PITFIT's HYROX classes break the race into its parts — strength on the stations, endurance in the runs, and the brutal skill of doing both at once — then rebuild them week by week until race day feels familiar.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/book" className="btn-primary">Book a HYROX Class <ArrowRight size={16} /></Link>
              <a href="#hyrox-schedule" className="btn-ghost">See the Schedule</a>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="glass-card p-7" style={{ borderColor: 'rgba(125,167,255,0.35)' }}>
              <div className="flex items-center gap-3 mb-5">
                <Trophy size={22} style={{ color: 'var(--pf-brand2)' }} />
                <h3 className="font-display font-bold text-white text-xl">{HYROX.race.name}</h3>
              </div>
              <div className="space-y-3 text-[15.5px]">
                <p className="text-muted-pf"><span className="text-white font-medium">When:</span> {HYROX.race.dates}</p>
                <p className="text-muted-pf"><span className="text-white font-medium">Where:</span> {HYROX.race.venue}</p>
                <p className="text-muted-pf"><span className="text-white font-medium">Prep window:</span> {HYROX.race.prepWeeks} of structured training</p>
              </div>
              <div className="mt-6 pt-6 border-t border-[rgba(148,170,220,0.13)]">
                <p className="text-[#cdd5e4] text-[15px] leading-relaxed">
                  Count backward from race week: a proper cycle takes {HYROX.race.prepWeeks}. If Dallas is your race, the right time to start training is now — and your first session costs nothing.
                </p>
                <Link to="/blog/hyrox-dallas-2026-prep-guide" className="btn-quiet mt-4">Read the full 16-week prep guide <ArrowRight size={14} /></Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THE STATIONS */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader
            tag="Know The Race"
            title="Every station, trained in the PIT."
            sub="Eight 1 km runs stitched between eight stations. Here's the full gauntlet — and how our HYROX classes attack each piece."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {HYROX.stations.map((s, i) => (
              <Reveal key={s.name} delay={i * 50}>
                <div className="glass-card glass-card-hover p-6 h-full">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display font-semibold text-white text-lg">{s.name}</h3>
                    <span className="font-mono-pf text-[12px] text-muted-pf">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <p className="text-muted-pf text-[14.5px] leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="text-muted-pf text-[15px] mt-8">
            Station deep dives from our coaches: <Link to="/blog/hyrox-wall-balls-technique" className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>wall ball technique and pacing</Link> · <Link to="/blog/hyrox-sled-push-pull-technique" className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>sled push & pull technique</Link> · <Link to="/blog/hyrox-burpee-broad-jumps-technique" className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>burpee broad jumps pacing</Link> · <Link to="/blog/hyrox-skierg-rowing-pacing" className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>SkiErg & rowing pacing</Link> · <Link to="/blog/hyrox-farmers-carry-sandbag-lunges" className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>farmers carry & sandbag lunges</Link> · <Link to="/blog/hyrox-compromised-running" className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>compromised running</Link> · <Link to="/blog/hyrox-doubles-vs-singles" className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>doubles vs singles</Link> · <Link to="/blog/hyrox-vs-hiit" className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>HYROX vs HIIT compared</Link>
          </p>
        </div>
      </section>

      {/* HYROX SCHEDULE */}
      <section id="hyrox-schedule" className="section-pad" style={{ background: 'var(--pf-bg2)' }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader
            tag="Weekly HYROX Schedule"
            title="12 HYROX classes. 6 days. Real coaches."
            sub="Every class matched with the coach who actually teaches it. Reserve any spot on the live booking board — your first class is $15."
          />
          <div className="space-y-3">
            {hyroxClasses.map((c, i) => (
              <Reveal key={`${c.day}-${c.time}-${c.name}`} delay={i * 40}>
                <div className="glass-card glass-card-hover px-6 py-5 grid sm:grid-cols-[110px_90px_1fr_auto] items-center gap-3 sm:gap-6">
                  <span className="font-mono-pf text-[12px] uppercase tracking-[0.14em]" style={{ color: 'var(--pf-brand2)' }}>{c.day}</span>
                  <span className="font-mono-pf text-[13px] text-white">{c.time}</span>
                  <div>
                    <p className="font-display font-semibold text-white text-[17px] leading-snug">{c.name}</p>
                    <p className="text-muted-pf text-[13.5px] mt-0.5">Coach {c.coach} · {c.name.includes('45') ? '45 min' : '60 min'}</p>
                  </div>
                  <Link
                    to={`/book?class=${encodeURIComponent(`${c.day} · ${c.time} · ${c.name}`)}`}
                    className="btn-ghost !min-h-[42px] !py-2 !px-5 text-[14px] justify-self-start sm:justify-self-end"
                  >
                    Reserve <ArrowRight size={14} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="font-mono-pf text-[12px] uppercase tracking-[0.12em] text-muted-pf mt-7">
            $15 first class · 2 weeks unlimited for $50 · included in every membership
          </p>
        </div>
      </section>

      {/* COACHES */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader
            tag="Your HYROX Coaches"
            title="The right class, the right coach."
            sub="No rotating substitutes reading a whiteboard. Each HYROX class on our schedule belongs to the coach who programs and teaches it."
          />
          <div className="grid md:grid-cols-3 gap-6">
            {coaches.map((t, i) => (
              <Reveal key={t.slug} delay={i * 80}>
                <div className="glass-card glass-card-hover overflow-hidden h-full flex flex-col">
                  <img src={t.img} alt={`${t.name} — HYROX coach at PITFIT Dallas`} className="w-full h-64 object-cover object-top" loading="lazy" data-testid={`hyrox-coach-image-${t.slug}`} />
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <h3 className="font-display font-bold text-white text-xl">{t.name}</h3>
                    </div>
                    <p className="mono-label mt-1.5">{t.role}</p>
                    <p className="text-muted-pf text-[14.5px] mt-3 leading-relaxed flex-1">{t.bio}</p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {t.classes.filter((c) => c.toUpperCase().includes('HYROX')).map((c) => (
                        <span key={c} className="font-mono-pf text-[10.5px] uppercase tracking-[0.1em] px-3 py-1.5 rounded-full border border-[rgba(148,170,220,0.22)] text-muted-pf">{c}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-9">
            <Link to="/about/trainers" className="btn-quiet">Meet the full coaching team <ArrowRight size={15} /></Link>
          </div>
        </div>
      </section>

      {/* WHY PITFIT FOR HYROX */}
      <section className="section-pad" style={{ background: 'var(--pf-bg2)' }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader
            tag="Why PITFIT"
            title="The easiest way to start HYROX in Dallas."
            sub="Other HYROX programs in town start with consultation calls and paid trial passes. Ours starts with a $15 first class you can book in half a minute."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {whyUs.map((w, i) => (
              <Reveal key={w.t} delay={i * 60}>
                <div className="glass-card glass-card-hover p-7 h-full">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(70,127,247,0.12)', border: '1px solid rgba(125,167,255,0.3)' }}>
                    <w.icon size={20} style={{ color: 'var(--pf-brand2)' }} />
                  </div>
                  <h3 className="font-display font-semibold text-white text-lg">{w.t}</h3>
                  <p className="text-muted-pf text-[15px] mt-2.5 leading-relaxed">{w.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVING AREAS */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader
            tag="HYROX Near You"
            title="One garage, all of central Dallas."
            sub="Travis Walk sits at the center of the neighborhoods that race: a short drive from almost anywhere inside the loop, with free parking in the building."
          />
          <div className="flex flex-wrap gap-2.5">
            {NEIGHBORHOODS.map((n) => (
              <Link key={n.slug} to={`/hiit-classes-dallas/${n.slug}`} className="font-mono-pf text-[12px] uppercase tracking-[0.12em] px-4 py-2.5 rounded-full border border-[rgba(148,170,220,0.25)] text-muted-pf hover:text-white hover:border-[rgba(125,167,255,0.5)] transition-colors">
                HYROX near {n.name}
              </Link>
            ))}
            <Link to="/locations/uptown-dallas" className="font-mono-pf text-[12px] uppercase tracking-[0.12em] px-4 py-2.5 rounded-full border border-[rgba(148,170,220,0.25)] text-muted-pf hover:text-white hover:border-[rgba(125,167,255,0.5)] transition-colors">
              Uptown Dallas studio
            </Link>
            <Link to="/hiit-classes-dallas" className="font-mono-pf text-[12px] uppercase tracking-[0.12em] px-4 py-2.5 rounded-full border border-[rgba(148,170,220,0.25)] text-muted-pf hover:text-white hover:border-[rgba(125,167,255,0.5)] transition-colors">
              HIIT classes Dallas
            </Link>
          </div>
          <div className="glass-card mt-9 p-7 flex flex-col md:flex-row md:items-center justify-between gap-5" style={{ borderColor: 'rgba(125,167,255,0.3)' }}>
            <div className="flex gap-4 items-start">
              <Timer size={22} className="shrink-0 mt-1" style={{ color: 'var(--pf-brand2)' }} />
              <div>
                <h3 className="font-display font-semibold text-white text-lg">Training for {HYROX.race.name}?</h3>
                <p className="text-muted-pf text-[14.5px] mt-1">{HYROX.race.dates} · {HYROX.race.venue}. A full prep cycle is {HYROX.race.prepWeeks} — start your countdown with a class this week.</p>
              </div>
            </div>
            <Link to="/book" className="btn-primary shrink-0">Book a Class <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad" style={{ background: 'var(--pf-bg2)' }}>
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <SectionHeader tag="HYROX FAQ" title="Everything Dallas asks about HYROX." center />
          <FAQAccordion faqs={HYROX.faqs} />
          <p className="text-muted-pf text-[15px] mt-9 text-center">
            Preparing for race day? Read the <Link to="/blog/hyrox-dallas-2026-prep-guide" className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>HYROX Dallas 2026 prep guide</Link>. More questions? <Link to="/contact" className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>Talk to the PIT</Link> — or browse the <Link to="/faq" className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>full FAQ</Link>.
          </p>
        </div>
      </section>

      <CloseCTA
        title="Your first HYROX class is $15."
        sub={`No consultation call, no membership, no commitment. Pick a class, show up 15 minutes early at ${STUDIO.address.split(',')[0]}, and start training for race day.`}
      />
    </>
  );
}
